import React from 'react';
import {
  MASKS_TYPE,
  getPrimaryDark,
  viewerTypes,
} from '../../constants/constants';
import {
  masksDefaultHeight,
  pageHeight,
  pageWidth,
} from '../../constants/gridConfig';
import { useStore } from '../../store/store';
import { getMaskById } from '../../store/lookups';
import { findPageBackground } from '../../utils/pageBackground';
import Container from '../Container/Container';

// Le décor de page à sa place : il couvre toute la page. L'en-tête et le pied
// sont montrés par-dessus, en retrait et sans interaction, comme repères ; une
// ligne marque leur limite, pour voir ce qui tombe dedans ou en déborde.
// `editable` : le décor se modifie (vue d'édition) ; sinon simple aperçu.

// Rappel posé au-dessus de la page pendant l'édition (hors de l'en-tête, pour
// ne pas le chevaucher) : rien de ce qu'on dessine ici ne se voit dans le
// document édité. La place est réservée par Middle (NOTICE_SPACE).
const EDITING_NOTICE =
  'Décor de page — visible uniquement dans l’aperçu PDF et le PDF généré';

export const NOTICE_SPACE = 32;

const noticeStyle = {
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: '100%',
  height: NOTICE_SPACE,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0 16px',
  textAlign: 'center',
  fontSize: 13,
  fontWeight: 600,
  pointerEvents: 'none',
};

const limitLabelStyle = {
  position: 'absolute',
  left: 4,
  fontSize: 11,
  fontWeight: 600,
  lineHeight: '14px',
};

const PageBackgroundFrame = ({ editable = false }) => {
  const header = useStore(({ masks }) =>
    getMaskById(masks, MASKS_TYPE.HEADER.id)
  );
  const footer = useStore(({ masks }) =>
    getMaskById(masks, MASKS_TYPE.FOOTER.id)
  );
  const background = useStore(({ masks }) => findPageBackground(masks));
  const landscape = useStore(({ landscape }) => landscape);
  // Même accent que le reste de l'éditeur (violet en modèle).
  const accentColor = useStore(({ currentDocument }) =>
    getPrimaryDark(currentDocument?.type === 'template')
  );

  // En-tête ou pied par-dessus le décor, avec la ligne de sa limite côté
  // contenu. Les clics le traversent : le décor dessous reste éditable.
  const maskOverlay = (mask, { edge, label }) => {
    if (!mask) return null;
    const height = mask.height ?? masksDefaultHeight[mask.id];
    return (
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          [edge]: 0,
          height,
          pointerEvents: 'none',
        }}
      >
        <div style={{ opacity: 0.4 }}>
          <Container id={mask.id} fromViewer={viewerTypes.PREVIEW} />
        </div>
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            [edge === 'top' ? 'bottom' : 'top']: 0,
            borderTop: `1px dashed ${accentColor}`,
          }}
        />
        <div
          style={{
            ...limitLabelStyle,
            // Côté en-tête / pied de la ligne.
            [edge === 'top' ? 'bottom' : 'top']: 2,
            color: accentColor,
          }}
        >
          {label}
        </div>
      </div>
    );
  };

  return (
    <div
      style={{
        position: 'relative',
        height: landscape ? pageWidth : pageHeight,
        backgroundColor: 'white',
      }}
    >
      {background && (
        <Container
          id={background.id}
          fromViewer={editable ? undefined : viewerTypes.PREVIEW}
        />
      )}
      {maskOverlay(header, { edge: 'top', label: 'En-tête' })}
      {maskOverlay(footer, { edge: 'bottom', label: 'Pied de page' })}
      {editable && (
        <div style={{ ...noticeStyle, color: accentColor }}>
          {EDITING_NOTICE}
        </div>
      )}
    </div>
  );
};

export default PageBackgroundFrame;
